import moment from 'moment';
import { TOOLTIP_DATE_READABLE, TOOLTIP_DATETIME_READABLE } from '@constants/index';
import { getValue, parseNumberToSignificant } from '@helpers';

export const mousePos = { x: 0, y: 0 };

export function updateMousePos(event) {
  mousePos.x = event.clientX + window.pageXOffset;
  mousePos.y = event.clientY + window.pageYOffset;
}

function isDayData(timestamp1, timestamp2) {
  const time1 = moment(timestamp1);
  const time2 = moment(timestamp2);

  // Calculate the difference in hours
  const diffInHours = Math.abs(time2.diff(time1, 'hours'));

  // Return true if more than 1 hour apart, otherwise false
  return diffInHours > 1;
}

export const getOrCreateTooltipDiv = (chart, theme, withButton) => {
  const {
    palette: {
      tooltip: { background, contrastText }
    }
  } = theme;
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.style.borderRadius = '3px';
    tooltipEl.style.opacity = 1;
    tooltipEl.style.visibility = 'visible';
    tooltipEl.style.fontSize = '14px';
    tooltipEl.style.pointerEvents = withButton ? 'auto' : 'none';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.textWrap = 'nowrap';
    tooltipEl.style.padding = '8px 16px';
    tooltipEl.style.minWidth = '300px';
    tooltipEl.style.transform = 'translate(-50%, 0)';
    tooltipEl.style.transition = 'all .1s ease';

    const body = document.createElement('div');
    body.style.display = 'flex';
    body.style.flexDirection = 'column';
    body.style.gap = '8px';

    tooltipEl.appendChild(body);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }
  tooltipEl.style.background = background;

  tooltipEl.style.color = contrastText;

  return tooltipEl;
};

const showSyncTooltip = (chartRef, index) => {
  const chart = chartRef.current;
  if (!chart) return;
  const tooltip = chart.tooltip;
  const element = chart.getDatasetMeta(0).data[index];
  if (element) {
    tooltip.setActiveElements([{ datasetIndex: 0, index }], {
      x: element.x,
      y: element.y
    });
  }
};

export const createTooltipLabelText = (labelText) => {
  const label = document.createElement('span');
  label.textContent = `${labelText}:`;
  label.style.fontFamily = '"Noto Sans", Roboto, helvetica, Arial, sans-serif';
  label.style.fontSize = '0.75rem';
  label.style.lineHeight = '1.66';
  return label;
};

export const createTooltipValueText = (textValue) => {
  const text = document.createElement('p');
  text.textContent = textValue;
  text.style.fontFamily = '"Noto Sans", Roboto, helvetica, Arial, sans-serif';
  text.style.fontSize = '0.875rem';
  text.style.lineHeight = '1.43';
  text.style.margin = '0';
  return text;
};

export const defaultTooltip = ({
  context,
  data,
  theme,
  unit,
  includeTooltipSum,
  hideEmptyValues,
  hideCurrentTooltip,
  syncRefs,
  handleLogClick = undefined,
  fixTooltipPosition = undefined,
  displayZeroValues = false,
  datasetMaxHeightPx = undefined,
  positionAboveChart = false,
  isHorizontal = false
}) => {
  if (!context) return;

  const {
    palette: {
      tooltip: { divider, contrastText }
    }
  } = theme;
  const { chart, tooltip } = context;

  const dataIndex = tooltip.dataPoints?.[0]?.dataIndex;
  if (hideCurrentTooltip) {
    syncRefs?.forEach((reference) => {
      showSyncTooltip(reference, dataIndex);
    });
    return;
  }

  const tooltipEl = getOrCreateTooltipDiv(chart, theme, !!handleLogClick);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    tooltipEl.style.visibility = 'hidden';
    return;
  }

  if (tooltip.body) {
    let title = tooltip.title || [];
    if (Array.isArray(tooltip)) {
      title = tooltip[0];
    }
    const headerText = formatTitle(data, title);
    const header = createHeaderElement(headerText);

    const tableBody = document.createElement('div');

    if (includeTooltipSum) {
      var sumElement = createSumElement(data, dataIndex, unit);
      
      if (sumElement) {
        tableBody.appendChild(sumElement);

        const dividerEl = getDividerElement(divider);
        tableBody.appendChild(dividerEl);
      }
    }

    const datasetContainer = buildDatasetContainer(data, theme, datasetMaxHeightPx, 
      dataIndex, displayZeroValues, unit, hideEmptyValues);

    tableBody.appendChild(datasetContainer);

    if (handleLogClick) {
      // Add the button to the tooltip
      const buttonContainer = addLogButton(contrastText, handleLogClick, title);
      tableBody.appendChild(buttonContainer);
    }

    const tableRoot = tooltipEl.querySelector('div');

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot.appendChild(header);
    tableRoot.appendChild(tableBody);
  }

  // Positioning the tooltip
  positionTooltip(chart, tooltip, tooltipEl, handleLogClick, fixTooltipPosition, positionAboveChart, isHorizontal);

  syncRefs?.forEach((reference) => {
    showSyncTooltip(reference, dataIndex);
  });
};

export const simpleBarChartTooltip = ({
  context,
  data,
  theme,
  title,
  hideCurrentTooltip,
  fixTooltipPosition,
  datasetMaxHeightPx,
  positionAboveChart,
  positionAboveCursor,
  isHorizontal = false
}) => {
  if (hideCurrentTooltip || !context) return;

  const { chart, tooltip } = context;
  const dataIndex = tooltip.dataPoints?.[0]?.dataIndex;

  const tooltipEl = getOrCreateTooltipDiv(chart, theme, false);

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    tooltipEl.style.visibility = 'hidden';
    return;
  }

  if (tooltip.body) {
    let tooltipTitle = title;
    if(!tooltipTitle) {
      let title = tooltip.title || [];
      if (Array.isArray(tooltip)) {
        title = tooltip[0];
      }

      tooltipTitle = formatTitle(data, title);
    }

    const header = createHeaderElement(tooltipTitle);
    const tableBody = document.createElement('div');

    const datasetContainer = buildSimpleDataSetContainer(data, dataIndex, datasetMaxHeightPx);

    tableBody.appendChild(datasetContainer);

    const tableRoot = tooltipEl.querySelector('div');

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot.appendChild(header);
    tableRoot.appendChild(tableBody);
  }

  // Positioning the tooltip
  positionTooltip(chart, tooltip, tooltipEl, false, fixTooltipPosition, positionAboveChart, positionAboveCursor, isHorizontal);
};

function formatTitle(data, title) {
  let format = TOOLTIP_DATETIME_READABLE;
  if (isDayData(data.labels[0], data.labels[1])) {
    format = TOOLTIP_DATE_READABLE;
  }

  const headerText = moment.utc(parseInt(title)).format(format);
  return headerText;
}

function positionTooltip(chart, tooltip, tooltipEl, handleLogClick, fixTooltipPosition, positionAboveChart, positionAboveCursor, isHorizontal = false) {
  let { offsetLeft: positionX } = chart.canvas;
  let left = positionX + tooltip.caretX;
  let halfWidth = chart.canvas.width / 2;

  try {
    if (isHorizontal && tooltip.dataPoints && tooltip.dataPoints.length) {
      // If the chart is horizontal (indexAxis === 'y'), position the tooltip at the end (right edge)
      // of the bar and vertically centered on the bar.

      positionForHorizontalBars();
    } else {
      positionTooltipToDefault();
    }
  } catch (e) {
    // Fallback to existing behavior on any unexpected error
    positionTooltipToDefault();
  }

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.visibility = 'visible';
  tooltipEl.style.position = 'absolute'; // Ensure absolute positioning
  tooltipEl.style.zIndex = '2000'; // Ensure it stays on top
  const zoomFactor = window.devicePixelRatio; // e.g., 1 for 100% zoom, 0.8 for 80%, 1.25 for 125%, etc.
  const actualCanvasWidth = chart.canvas.width / zoomFactor;

  if (handleLogClick || fixTooltipPosition) {
    const tooltipBound = tooltipEl.getBoundingClientRect();
    const tooltipHalfWidth = tooltipBound.width / 2;
    if (tooltipHalfWidth > left) {
      tooltipEl.style.left = `${tooltipHalfWidth}px`;
    } else if (left + tooltipHalfWidth > actualCanvasWidth) {
      tooltipEl.style.left = `${actualCanvasWidth - tooltipHalfWidth}px`;
    } else {
      tooltipEl.style.left = `${left}px`;
    }

    tooltipEl.style.top = `-${tooltipBound.height / 2}px`;

    tooltipEl.addEventListener('mouseout', () => {
      tooltipEl.style.opacity = 0;
      tooltipEl.style.visibility = 'hidden';
    });

    tooltipEl.addEventListener('mouseover', () => {
      tooltipEl.style.visibility = 'visible';
      tooltipEl.style.opacity = 1;
    });
  }

  if (positionAboveChart && !isHorizontal) {
    const tooltipBound = tooltipEl.getBoundingClientRect();
    tooltipEl.style.top = `-${tooltipBound.height}px`;
  }

  if (positionAboveCursor && !isHorizontal) {
    const tooltipBound = tooltipEl.getBoundingClientRect();
    const canvasPosition = chart.canvas.getBoundingClientRect();
    tooltipEl.style.top = `${mousePos.y - 20 - canvasPosition.top - tooltipBound.height - window.scrollY}px`
  }

  function positionForHorizontalBars() {
    const dp = tooltip.dataPoints[0];
    const meta = chart.getDatasetMeta(dp.datasetIndex);
    const element = meta?.data?.[dp.dataIndex];
    if (element) {
      const rightEdge = Math.max(element.x ?? 0, element.base ?? 0);
      const canvasRect = chart.canvas.getBoundingClientRect();
      const parentRect = chart.canvas.parentNode.getBoundingClientRect();

      // desired left edge (relative to parent) is rightEdge + small gap
      const gap = 8; // px gap between bar end and tooltip
      const desiredLeftEdge = canvasRect.left + rightEdge - parentRect.left + gap;

      // measure tooltip size
      const tooltipBound = tooltipEl.getBoundingClientRect();
      const tooltipHalfWidth = tooltipBound.width / 2;

      // because the tooltip uses transform: translate(-50%, 0) we need to set left to desiredLeftEdge + halfWidth
      let leftPos = desiredLeftEdge + tooltipHalfWidth;

      // clamp so tooltip stays inside parent bounds (sticky to end)
      const minLeft = tooltipHalfWidth;
      const maxLeft = parentRect.width - tooltipHalfWidth;
      if (leftPos < minLeft) leftPos = minLeft;
      if (leftPos > maxLeft) leftPos = maxLeft;

      // vertical center over the bar
      const tooltipHeight = tooltipBound.height;
      const topPos = canvasRect.top + (element.y ?? tooltip.caretY) - tooltipHeight / 2 - parentRect.top;

      tooltipEl.style.left = `${leftPos}px`;
      tooltipEl.style.top = `${topPos}px`;
    }
  }

  function positionTooltipToDefault() {
    if (left < halfWidth) {
      tooltipEl.style.left = `${left - 20 + tooltipEl.getBoundingClientRect().width / 2}px`;
    } else {
      tooltipEl.style.left = `${left + 20 - tooltipEl.getBoundingClientRect().width / 2}px`;
    }

    // tooltipEl.style.top = `${positionY + tooltip.caretY}px`;
    tooltipEl.style.top = `-${tooltipEl.getBoundingClientRect().height / 2}px`;
  }
}

function createHeaderElement(title) {
  const header = document.createElement('div');
  header.style.fontWeight = 'bold';
  header.style.fontSize = '16px';
  header.textContent = title;
  return header;
}

function buildSimpleDataSetContainer(data, dataIndex) {
  const datasetContainer = data?.datasets ? document.createElement('div') : undefined;
  const dataset = data.datasets[0];
  const label = data.labels[dataIndex];
  const backgroundColor = dataset.backgroundColor[dataIndex];
  const value = dataset.data[dataIndex];
  const unit = dataset.unit;

  const dataRow = addDataRow("bar", backgroundColor, undefined, label, value, true, false, undefined, unit, false, datasetContainer);
  if (dataRow)
    datasetContainer.appendChild(dataRow);

  return datasetContainer;
}

export const addDetailRow = (container, labelText, textValue) => {
  const row = document.createElement('div');
  row.style.display = 'flex';
  row.style.justifyContent = 'space-between';
  row.style.marginBottom = '4px';

  const label = document.createElement('span');
  label.textContent = `${labelText}:`;
  label.style.fontFamily = '"Noto Sans", Roboto, helvetica, Arial, sans-serif';
  label.style.fontSize = '0.75rem';
  label.style.lineHeight = '1.66';
  row.appendChild(label);

  const text = document.createElement('span');
  text.textContent = textValue;
  text.style.fontFamily = '"Noto Sans", Roboto, helvetica, Arial, sans-serif';
  text.style.fontSize = '0.875rem';
  text.style.lineHeight = '1.43';
  text.style.margin = '0';
  row.appendChild(text);

  container.appendChild(row);
};

export const defaultBarTooltip = (context, data, theme) => {
  const { chart, tooltip } = context;

  const {
    palette: {
      tooltip: { background, contrastText }
    }
  } = theme;

  let tooltipEl = document.getElementById('chartjs-custom-tooltip');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'chartjs-custom-tooltip';
    tooltipEl.style.borderRadius = '3px';
    tooltipEl.style.fontSize = '14px';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.textWrap = 'nowrap';
    tooltipEl.style.padding = '8px 16px';
    tooltipEl.style.minWidth = '300px';
    tooltipEl.style.transform = 'translate(-50%, 0)';
    tooltipEl.style.transition = 'all .1s ease';
    tooltipEl.style.background = background;
    tooltipEl.style.color = contrastText;
    tooltipEl.style.boxShadow = '0px 4px 8px rgba(0, 0, 0, 0.1)';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.zIndex = '2000';
    document.body.appendChild(tooltipEl);
  }

  if (!tooltip || tooltip.opacity === 0) {
    tooltipEl.style.display = 'none';
    return;
  }

  const tooltipData = tooltip.dataPoints[0];
  const { datasetIndex } = tooltipData;
  const event = data[datasetIndex];

  const start = moment(event.startDate).format('YYYY-MM-DD HH:mm');
  const end = moment(event.endDate).format('YYYY-MM-DD HH:mm');
  const duration = moment.duration(moment(event.endDate).diff(moment(event.startDate)));
  const days = Math.floor(duration.asDays());
  const hours = duration.hours();
  const minutes = duration.minutes();

  tooltipEl.innerHTML = '';

  const header = document.createElement('div');
  header.style.fontWeight = '600';
  header.style.marginBottom = '8px';
  header.style.fontSize = '14px';
  header.style.letterSpacing = '0.1px';
  header.textContent = event.eventName;
  tooltipEl.appendChild(header);

  const details = document.createElement('div');

  addDetailRow(details, 'Start', start);
  addDetailRow(details, 'End', end);
  addDetailRow(details, 'Duration', `${days}d ${hours}h ${minutes}m`);
  addDetailRow(details, 'Potential savings', event.saving);
  addDetailRow(details, 'Potential cost savings', event.potentialSaving);

  tooltipEl.appendChild(details);

  const position = chart.canvas.getBoundingClientRect();
  const caretPadding = 16;
  const tooltipX = position.left + tooltip.caretX;
  const tooltipY = position.top + window.pageYOffset + tooltip.caretY + caretPadding;
  let halfWidth = chart.canvas.width / 2;
  if (tooltipX < halfWidth) {
    tooltipEl.style.left = `${tooltipX}px`;
  } else {
    tooltipEl.style.left = `${tooltipX - 130}px`;
  }

  tooltipEl.style.display = 'block';
  tooltipEl.style.top = `${tooltipY}px`;
};

function createSumElement(data, dataIndex, unit) {
  let sum = 0;
  data.datasets.forEach(({ data, hidden }) => {
    sum += hidden ? 0 : getValue(data[dataIndex]?.y);
  });

  // Check if sum is a valid number
  if (!isNaN(sum)) {
    const content = document.createElement('div');
    content.style.display = 'flex';
    content.style.justifyContent = 'space-between';
    content.style.gap = '16px';

    let valueText = createTooltipValueText(
      `${parseNumberToSignificant(parseFloat(sum))} ${unit}`
    );
    let sumText = createTooltipValueText(`Sum`);

    content.appendChild(sumText);
    content.appendChild(valueText);

    return content;
  }
  return undefined;
}

function getDividerElement(divider) {
  const dividerEl = document.createElement('div');
  dividerEl.style.width = `100%`;
  dividerEl.style.height = `1px`;
  dividerEl.style.paddingBottom = `8px`;
  dividerEl.style.borderTop = `1px solid ${divider}`;

  return dividerEl;
}

function addLogButton(contrastText, handleLogClick, title) {
  const buttonContainer = document.createElement('div');
  buttonContainer.style.display = 'flex';
  buttonContainer.style.width = '100%';
  buttonContainer.style.placeContent = 'center';
  buttonContainer.style.flexDirection = 'row';
  const button = document.createElement('button');
  button.textContent = 'SEE LOG';
  button.style.marginTop = '10px'; // Add spacing to separate from content
  button.style.padding = '8px 16px';
  button.style.border = '1px solid';
  button.style.borderColor = contrastText;
  button.style.background = 'transparent';
  button.style.borderRadius = '100px';
  button.style.color = contrastText;
  button.style.cursor = 'pointer';
  button.style.fontSize = '14px';
  button.style.minWidth = '63px'; // Make the button span the width of the tooltip
  button.style.textAlign = 'center';

  // Add event listener if handleLogClick is provided
  button.addEventListener('click', () => {
    handleLogClick(title[0]);
  });

  // Append the button to the tooltip
  buttonContainer.appendChild(button);
  return buttonContainer;
}

function buildDatasetContainer(data, theme, datasetMaxHeightPx, dataIndex, displayZeroValues, unit, hideEmptyValues) {
  const datasetContainer = data?.datasets ? document.createElement('div') : undefined;
  const isDark = theme.palette.mode === 'dark';

  if (datasetMaxHeightPx) {
    datasetContainer.style.overflow = 'auto';
    datasetContainer.style.maxHeight = `${datasetMaxHeightPx}px`;
    datasetContainer.style.paddingRight = '5px';
    datasetContainer.style.scrollbarWidth = 'thin';
    datasetContainer.style.scrollbarColor = `${isDark ? '#1a1a1a #f0f0f0' : '#f0f0f0 #1a1a1a'}`;
  }

  // Create content for each point and line
  data?.datasets?.forEach(
    ({ data, label, borderColor, type, hidden, backgroundColor, unit: hiddenUnit }) => {
      const value = data[dataIndex]?.y;
      const dataRow = addDataRow(type, backgroundColor, borderColor, label, value, displayZeroValues, hidden, hiddenUnit, unit, hideEmptyValues);
      if (dataRow)
        datasetContainer.appendChild(dataRow);
    }
  );
  return datasetContainer;
}

function addDataRow(type, backgroundColor, borderColor, label, value, displayZeroValues, hidden, hiddenUnit, unit, hideEmptyValues) {
  const content = document.createElement('div');
  content.style.display = 'flex';
  content.style.justifyContent = 'space-between';
  content.style.gap = '16px';

  const legend = document.createElement('div');
  legend.style.display = 'flex';
  legend.style.gap = '8px';
  legend.style.alignItems = 'center';
  legend.style.flexDirection = 'row';

  const legendBox = document.createElement('span');
  legendBox.style.background = type === 'bar' ? backgroundColor : borderColor;
  legendBox.style.height = '8px';
  legendBox.style.width = '8px';
  legendBox.style.display = 'inline-block';
  const title = createTooltipLabelText(label);

  let valueText;

  if (value || (displayZeroValues && value == 0)) {
    valueText = createTooltipValueText(
      `${parseNumberToSignificant(parseFloat(value))} ${hidden ? hiddenUnit : unit}`
    );
  } else {
    valueText = createTooltipValueText('-');
  }

  if (!hideEmptyValues || value || (value === 0 && displayZeroValues)) {
    legend.appendChild(legendBox);
    legend.appendChild(title);
    content.appendChild(legend);
    content.appendChild(valueText);
    return content;
  }
  return undefined;
}

